// routes
import {
  PATH_ADMIN,
  PATH_DASHBOARD,
  PATH_LEARNING_SCIENCE,
  PATH_LEARN,
  PATH_CHECK_CHART,
} from './paths';
// components
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SchoolIcon from '@mui/icons-material/School';
import MoneyIcon from '@mui/icons-material/LocalAtm';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BallotIcon from '@mui/icons-material/Ballot';
import { AuthUser, Role } from '@common/@types/auth';
import Iconify from '@common/components/Iconify';
import React from 'react';
import SvgIconStyle from '@common/components/SvgIconStyle';
import { BlacklistFunction } from '@common/@types/nav';
import { SiteStrings } from 'common/@types/siteStrings';

// ----------------------------------------------------------------------

export function blockUserNotInRoles(...roles: Role[]): BlacklistFunction {
  return (user: AuthUser) => roles.every((role) => user?.alphaAuthAttributes?.role !== role);
}

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

export const getNavConfig = (siteStrings: SiteStrings) => [
  {
    subheader: 'learn',
    items: [
      {
        title: 'my required learning',
        path: PATH_LEARN.general.recommendations,
        icon: <SchoolIcon />,
      },
    ],
  },
  {
    subheader: 'dashboard',
    items: [
      {
        title: 'my daily activity',
        path: PATH_DASHBOARD.general.daily_activity.day,
        icon: (
          <Iconify
            icon={
              siteStrings.nav.dashboard.myActivity.icon ?? 'mdi:clipboard-check-multiple-outline'
            }
            width={32}
            height={32}
          />
        ),
        children: [
          {
            title: 'daily',
            path: PATH_DASHBOARD.general.daily_activity.day,
          },
          {
            title: 'weekly',
            path: PATH_DASHBOARD.general.daily_activity.week,
          },
          {
            title: 'monthly',
            path: PATH_DASHBOARD.general.daily_activity.month,
          },
        ],
      },
      {
        title: 'earnings',
        path: PATH_DASHBOARD.general.learn_to_earn,
        icon: <MoneyIcon />,
        blacklistFunction: (user: AuthUser) =>
          user?.alphaAuthAttributes?.campusId !== '4' &&
          user?.alphaAuthAttributes?.campusId !== '9' &&
          user?.alphaAuthAttributes?.role !== Role.ADMIN,
      },
      {
        title: 'my session',
        path: PATH_DASHBOARD.general.session,
        icon: getIcon('ic_analytics'),
        blacklistFunction: (user: AuthUser) =>
          // We disable untill we have data and open it back
          true,
      },
      {
        title: 'leaderboards',
        path: PATH_DASHBOARD.general.leaderboards.units_mastered,
        icon: <Iconify icon={'ant-design:trophy-outlined'} width={32} height={32} />,
        children: [
          {
            title: '2hr sessions',
            path: PATH_DASHBOARD.general.leaderboards.sessions_2hr,
          },
          {
            title: 'mastered units',
            path: PATH_DASHBOARD.general.leaderboards.units_mastered,
          },
          {
            title: 'beast of the week',
            path: PATH_DASHBOARD.general.leaderboards.beast_of_the_week,
          },
          {
            title: 'learning blitz',
            path: PATH_DASHBOARD.general.leaderboards.learning_blitz,
            blacklistFunction: (user: AuthUser) => {
              const role = user?.alphaAuthAttributes?.role;
              return role == null || ![Role.ADMIN, Role.GUIDE].includes(role);
            },
          },
          {
            title: 'learn to give',
            path: PATH_DASHBOARD.general.leaderboards.learning_to_give,
            blacklistFunction: (user: AuthUser) =>
              user?.alphaAuthAttributes?.campusId === '4' ||
              user?.alphaAuthAttributes?.campusId === '9',
          },
          // AI-GEN START - JB AI Assistant - GPT4
          {
            title: 'Math Facts Challenge',
            path: PATH_DASHBOARD.general.leaderboards.math_facts_challenge,
            blacklistFunction: (user: AuthUser) =>
              user?.alphaAuthAttributes?.campusId === '4' ||
              user?.alphaAuthAttributes?.campusId === '9',
          },
          // AI-GEN END
        ],
        blacklistFunction: (user: AuthUser) =>
          // enabled for everyone except parents
          user?.alphaAuthAttributes?.campusId === '4' ||
          user?.alphaAuthAttributes?.campusId === '9' ||
          user?.alphaAuthAttributes?.role === Role.PARENT,
      },
      {
        title: 'my accountability',
        path: PATH_DASHBOARD.general.accountability,
        icon: <Iconify icon={'mdi:thought-bubble-outline'} width={32} height={32} />,
        blacklistFunction: (user: AuthUser) =>
          /*const isNotL2 =
              user?.alphaAuthAttributes?.levels.every(
                (level: Level) =>
                  level.displayName !== LevelDisplayName.L2 &&
                  level.displayName !== LevelDisplayName.Multilevel
              ) ||
              (user?.alphaAuthAttributes?.campusId != '1' &&
                user?.alphaAuthAttributes?.campusId != '>=0') ||
              user?.alphaAuthAttributes?.role === Role.PARENT;*/
          true,
      },
    ],
  },
  {
    subheader: siteStrings.nav.checkCharts.subheader ?? 'check charts',
    items: [
      {
        title: siteStrings.nav.checkCharts.checkCharts.title ?? 'check charts',
        path: PATH_CHECK_CHART.root,
        icon: <Iconify icon={siteStrings.nav.checkCharts.checkCharts.icon ?? 'mdi:chart-box'} />,
        blacklistFunction: (user: AuthUser) =>
          user?.alphaAuthAttributes?.campusId === '4' ||
          user?.alphaAuthAttributes?.campusId === '9',
      },
      {
        title: siteStrings.nav.checkCharts.checkChartsStudent.title ?? 'check charts - student',
        path: PATH_CHECK_CHART.general.student,
        icon: (
          <Iconify icon={siteStrings.nav.checkCharts.checkChartsStudent.icon ?? 'mdi:chart-box'} />
        ),

        // Permanent: only show to guides/admins; students/parents load their view from above link
        blacklistFunction: (user: AuthUser) =>
          user?.alphaAuthAttributes?.role === Role.STUDENT ||
          user?.alphaAuthAttributes?.role === Role.PARENT,
      },
    ],
  },
  {
    subheader: 'learning science',
    items: [
      {
        title: 'Session Snapshot',
        path: PATH_LEARNING_SCIENCE.general.snapshot,
        icon: <CameraAltIcon />,
        blacklistFunction: (user: AuthUser) => {
          const role = user?.alphaAuthAttributes?.role;

          // Safety
          if (!role) return true;

          // Enabled for guides/admins/parents
          if ([Role.ADMIN, Role.GUIDE, Role.PARENT].includes(role)) {
            return false;
          }

          // Hidden for all L4s
          if (user?.alphaAuthAttributes?.levels.some((level) => level.displayName === 'L4')) {
            return true;
          }

          // Enabled only for Austin & Brownsville
          return (
            user?.alphaAuthAttributes?.campusId !== '1' &&
            user?.alphaAuthAttributes?.campusId !== '3'
          );
        },
      },
      {
        title: 'my learning report',
        path: PATH_LEARNING_SCIENCE.general.learning_report,
        icon: <Iconify icon={'mdi:clipboard-check-multiple-outline'} width={32} height={32} />,
        blacklistFunction: (user: AuthUser) => {
          const role = user?.alphaAuthAttributes?.role;
          return (
            role == null ||
            user?.alphaAuthAttributes?.campusId === '4' ||
            user?.alphaAuthAttributes?.campusId === '9' ||
            user?.alphaAuthAttributes?.campusId === '6'
          );
        },
      },
      {
        title: 'my MAP report',
        path: PATH_LEARNING_SCIENCE.general.map,
        icon: <Iconify icon={siteStrings.nav.learningScience.myMapReport.icon ?? 'mdi:school'} />,
      },
      {
        title: 'panorama report',
        path: PATH_LEARNING_SCIENCE.general.panorama,
        icon: <BallotIcon />,
        blacklistFunction: (user: AuthUser) => {
          const role = user?.alphaAuthAttributes?.role;
          return role == null || ![Role.ADMIN, Role.PARENT, Role.GUIDE].includes(role);
        },
      },
      {
        title: 'my gaps',
        path: PATH_LEARNING_SCIENCE.general.gaps,
        icon: <TrackChangesIcon />,
        // 2023-08: temporarily disabled until we figure out a way to populate them
        blacklistFunction: (user: AuthUser) => true,
      },
    ],
  },
  {
    subheader: 'admin',
    items: [
      {
        title: 'survey results',
        path: PATH_ADMIN.general.survey_results,
        icon: <Iconify icon={'mdi:clipboard-edit-outline'} width={32} height={32} />,
        blacklistFunction: (user: AuthUser) =>
          user?.alphaAuthAttributes?.role !== Role.ADMIN &&
          user?.alphaAuthAttributes?.role !== Role.GUIDE,
      },
    ],
  },
];
